import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Sun, Moon, Monitor } from 'lucide-react';
import { setMenuLanguage } from '../i18n';
import { useSession } from '../context/SessionContext';
import { useTheme } from '../context/ThemeContext';
import { useFavorites } from '../context/FavoritesContext';
import { useCart } from '../context/CartContext';
import './AccountPage.css';

function AccountPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { session, endSession } = useSession();
  const { preference, cyclePreference } = useTheme();
  const { favoritesCount } = useFavorites();
  const { clearCart } = useCart();
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const languages = [
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'pt', name: 'Português', flag: '🇧🇷' }
  ];

  const currentLanguage = languages.find(l => l.code === i18n.language) || languages[0];

  // Theme: cycle light -> dark -> auto. Icono + label segun la preferencia.
  const themeMeta = {
    light: { Icon: Sun, label: t('theme.light') },
    dark: { Icon: Moon, label: t('theme.dark') },
    auto: { Icon: Monitor, label: t('theme.auto') },
  };
  const { Icon: ThemeIcon, label: themeLabel } = themeMeta[preference] || themeMeta.auto;

  const handleLanguageChange = (langCode) => {
    i18n.changeLanguage(langCode);
    setMenuLanguage(langCode);
    setShowLanguageMenu(false);
  };

  const handleLogout = () => {
    const currentRestaurantId = session?.restaurantId;
    const currentTable = session?.tableNumber;
    clearCart();
    endSession();
    setShowLogoutConfirm(false);
    // Navigate back to the restaurant menu (public, no session needed)
    if (currentRestaurantId && currentTable) {
      navigate(`/r/${currentRestaurantId}/t/${currentTable}`);
    } else if (currentRestaurantId) {
      navigate(`/r/${currentRestaurantId}/takeaway`);
    } else {
      navigate('/');
    }
  };

  const handleGoToOrders = () => {
    navigate('/orders');
  };

  const handleGoToFavorites = () => {
    // Navegar al menú con filtro de favoritos si hay sesión
    if (session?.tableNumber) {
      navigate(`/r/${session.restaurantId}/t/${session.tableNumber}?favorites=true`);
    } else if (session?.restaurantId) {
      navigate(`/r/${session.restaurantId}/takeaway?favorites=true`);
    }
  };

  // Usuario display
  const customerName = session?.customer?.name || session?.customerName || t('auth.guest');
  const customerPhone = session?.customer?.phone || session?.customerPhone || '';
  const customerInitial = customerName.charAt(0).toUpperCase();

  return (
    <div className="account-page">
      {/* Header */}
      <div className="account-header">
        <button onClick={() => navigate(-1)} className="back-btn">
          <span className="material-icons" aria-hidden="true">arrow_back</span>
        </button>
        <h1>{t('account.title')}</h1>
        <div className="header-spacer"></div>
      </div>

      {/* User Profile Section */}
      <div className="account-profile">
        <div className="account-avatar">
          <span>{customerInitial}</span>
        </div>
        <div className="account-info">
          <h2 className="account-name">{customerName}</h2>
          {customerPhone && (
            <p className="account-phone">
              <span className="material-icons" aria-hidden="true">phone</span>
              {customerPhone}
            </p>
          )}
        </div>
      </div>

      {/* Menu Links */}
      <div className="account-section">
        <h3 className="section-title">{t('account.myActivity')}</h3>

        <button className="account-menu-item" onClick={handleGoToOrders}>
          <div className="menu-item-left">
            <span className="material-icons" aria-hidden="true">receipt_long</span>
            <span>{t('orders.title')}</span>
          </div>
          <span className="material-icons" aria-hidden="true">chevron_right</span>
        </button>

        <button className="account-menu-item" onClick={handleGoToFavorites}>
          <div className="menu-item-left">
            <span className="material-icons" aria-hidden="true">favorite</span>
            <span>{t('favorites.title')}</span>
          </div>
          <div className="menu-item-right">
            {favoritesCount > 0 && (
              <span className="menu-item-badge">{favoritesCount}</span>
            )}
            <span className="material-icons" aria-hidden="true">chevron_right</span>
          </div>
        </button>
      </div>

      {/* Settings */}
      <div className="account-section">
        <h3 className="section-title">{t('account.settings')}</h3>

        {/* Language Selector */}
        <div className="account-menu-item" onClick={() => setShowLanguageMenu(!showLanguageMenu)}>
          <div className="menu-item-left">
            <span className="material-icons" aria-hidden="true">language</span>
            <span>{t('language.select')}</span>
          </div>
          <div className="menu-item-right">
            <span className="menu-item-value">
              {currentLanguage.flag} {currentLanguage.name}
            </span>
            <span className="material-icons" aria-hidden="true">
              {showLanguageMenu ? 'expand_less' : 'expand_more'}
            </span>
          </div>
        </div>

        {/* Language Options */}
        {showLanguageMenu && (
          <div className="language-options">
            {languages.map(lang => (
              <button
                key={lang.code}
                className={`language-option ${i18n.language === lang.code ? 'active' : ''}`}
                onClick={() => handleLanguageChange(lang.code)}
              >
                <span className="language-flag">{lang.flag}</span>
                <span className="language-name">{lang.name}</span>
                {i18n.language === lang.code && (
                  <span className="material-icons" aria-hidden="true">check</span>
                )}
              </button>
            ))}
          </div>
        )}

        {/* Theme Toggle - cycle light -> dark -> auto */}
        <button
          className="account-menu-item"
          onClick={cyclePreference}
          aria-label={`${t('theme.label')}: ${themeLabel}`}
        >
          <div className="menu-item-left">
            <ThemeIcon className="menu-item-lucide" size={22} aria-hidden="true" />
            <span>{t('theme.label')}</span>
          </div>
          <div className="menu-item-right">
            <span className="menu-item-value">{themeLabel}</span>
          </div>
        </button>
      </div>

      {/* Session Info */}
      {session && (
        <div className="account-section">
          <h3 className="section-title">{t('account.session')}</h3>

          <div className="session-info-card">
            <div className="session-info-row">
              <span className="material-icons" aria-hidden="true">store</span>
              <span>{session.restaurantName || t('account.restaurant')}</span>
            </div>
            {session.tableNumber && (
              <div className="session-info-row">
                <span className="material-icons" aria-hidden="true">table_restaurant</span>
                <span>{t('orders.table')} {session.tableNumber}</span>
              </div>
            )}
            {!session.tableNumber && (
              <div className="session-info-row">
                <span className="material-icons" aria-hidden="true">takeout_dining</span>
                <span>{t('nav.takeaway')}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Logout */}
      {session && (
        <div className="account-section">
          <button
            className="logout-btn"
            onClick={() => setShowLogoutConfirm(true)}
          >
            <span className="material-icons" aria-hidden="true">logout</span>
            <span>{t('account.logout')}</span>
          </button>
        </div>
      )}

      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div className="modal-overlay" onClick={() => setShowLogoutConfirm(false)}>
          <div className="logout-modal" onClick={e => e.stopPropagation()}>
            <h3>{t('account.logoutConfirmTitle')}</h3>
            <p>{t('account.logoutConfirmMessage')}</p>
            <div className="logout-modal-actions">
              <button
                className="btn-cancel"
                onClick={() => setShowLogoutConfirm(false)}
              >
                {t('common.cancel')}
              </button>
              <button
                className="btn-logout"
                onClick={handleLogout}
              >
                {t('account.logout')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* App Version */}
      <div className="app-version">
        <p>{t('account.appVersion')}</p>
      </div>
    </div>
  );
}

export default AccountPage;
